import {AfterViewInit, Component, computed, ElementRef, inject, input, LOCALE_ID, signal, Signal} from '@angular/core';
import {formatDate} from '@angular/common';

import {BaseChartDirective} from 'ng2-charts';
import {Chart, ChartConfiguration, ChartData} from 'chart.js';

import {DataBatchTools, Formatter} from '@selpia/shared/utils';
import {Alignment, DataPoint, Measure} from '@selpia/shared/models';

export interface Range {
  min: number;
  max: number;
}

@Component({
  selector: 'lib-l12m-bar-chart',
  imports: [BaseChartDirective],
  templateUrl: './l12m-bar-chart.component.html',
  styleUrl: './l12m-bar-chart.component.scss',
})
export class L12mBarChartComponent implements AfterViewInit {
  readonly #locale = inject(LOCALE_ID);

  readonly dataPoints = input.required<DataPoint[]>();
  readonly growthMeasure = input.required<Measure | undefined>();
  readonly period = input.required<string | undefined>();
  readonly date = input.required<Date | undefined>();
  readonly title = input<string>();
  readonly range = input<Range>();
  readonly isHighlighted = input<boolean>();

  readonly isReady = signal(false);

  constructor(private elementRef: ElementRef) {
    Chart.defaults.font.family = 'Poppins';
  }

  ngAfterViewInit(): void {
    this.isReady.set(true);
  }

  protected readonly chartData: Signal<ChartData<'bar'> | undefined> = computed(() => {
    this.isHighlighted();

    if (!this.isReady()) {
      return;
    }

    const horizon = [...Array(12)
      .keys()];
    const focusDate = this.date();

    const labels = new Array<string>();
    const points = new Array<number | null>();

    const isAligned = this.growthMeasure()?.alignment === Alignment.Aligned;

    const negativeColor = this.#getCSS('--negative-chart-bar');
    const positiveColor = this.#getCSS('--positive-chart-bar');

    const outlineColor = this.#getCSS('--chart-outline-color');
    const negativeBg = outlineColor ? 'transparent' : (isAligned ? negativeColor : positiveColor);
    const positiveBg = outlineColor || (isAligned ? positiveColor : negativeColor);

    const data: ChartData<'bar'> = {
      labels,
      datasets: [{
        data: points,
        backgroundColor({dataset, dataIndex}) {
          const value = dataset.data[dataIndex] as number | null;
          if (value && value > 0) {
            return positiveBg;
          }
          return negativeBg;
        },
        borderColor: outlineColor,
        borderWidth({dataset, dataIndex}) {
          const value = dataset.data[dataIndex] as number | null;
          if (outlineColor && value && value < 0) {
            return 1;
          }
          return 0;
        },
      }],
    };

    if (!focusDate) {
      return data;
    }

    const date = new Date(focusDate);

    horizon.forEach(() => {
      const point = DataBatchTools.getDataPointFor(this.dataPoints(), {
        measureCode: this.growthMeasure()?.code,
        periodCode: this.period(),
        date,
      });


      points.unshift(point?.value || null);

      labels.unshift(formatDate(date, "MMM’YY", this.#locale));

      date.setDate(0);
    });

    return data;
  });

  protected readonly barChartOptions: Signal<ChartConfiguration<'bar'>['options']> = computed(() => {
    this.isHighlighted();

    if (!this.isReady()) {
      return;
    }

    const scaleColor = this.#getCSS('--chart-scale-color');
    const textColor = this.#getCSS('--chart-text-color');
    const numberFont = this.#getCSS('--number-font');
    const title = this.title();
    const growthMeasure = this.growthMeasure();
    const range = this.range();

    return {
      barPercentage: 1,
      categoryPercentage: 0.9,
      maintainAspectRatio: false,
      responsive: true,
      layout: {
        padding: {
          left: 41,
          right: 41,
          top: 0,
          bottom: title ? 0 : -14,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            callback: function (_, index, ticks) {
              if (index === 0 || index === ticks.at(-1)?.value) {
                return this.getLabelForValue(index);
              }
              return undefined;
            },
            padding: title ? 0 : 16,
            color: textColor,
            font: {
              family: numberFont,
              size: 14,
            },
          },
          border: {
            display: false,
          },
        },
        y: {
          suggestedMin: range?.min,
          suggestedMax: range?.max,
          grace: 0,
          grid: {
            drawTicks: false,
            color: scaleColor,
            lineWidth: function (context) {
              if (context.tick.value === 0) {
                return 1;
              }
              return 0;
            },
          },
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          displayColors: false,
          cornerRadius: 2,
          bodyAlign: 'center',
          callbacks: {
            label: function (context) {
              const value = context.raw as number;
              return Formatter.formatByMeasure(value, growthMeasure, true);
            },
          },
        },
        title: {
          display: !!title,
          color: textColor,
          position:
            'bottom',
          text: title,
          font:
            {
              size: 10,
              weight:
                'normal',
            }
          ,
          padding: {
            top: 10,
            bottom:
              0,
          }
          ,
        },
      },
    }
      ;
  });

  #getCSS(variableName: string): string {
    return getComputedStyle(this.elementRef.nativeElement)
      .getPropertyValue(variableName);
  }
}
